import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { SearchRegular, ArrowSyncRegular, AddRegular, EditRegular, EyeRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { ProjectsApi, type ProjectCategory } from "@/services/api/projects-api";
import { getCreateDealDisplay, openCreateDealDialog } from "@/services/outlook/create-deal-dialog";
import { getWorkflowAccess } from "@/services/auth/role-privileges";
import { useAuth } from "@/services/auth/use-auth";
import type { TriyoProject } from "@/types/models";
import { formatLongCalendarDate } from "@/utils/calendar-date";
import { TabSectionHeader } from "@/components/Workflow/TabSectionHeader";
import { CreateDeal } from "@/components/Workflow/QuickActions/CreateDeal";
import { DealDetails } from "./DealDetails";
import { hasDealRelationship } from "./deal-access";
import "../shared.scss";
import "./Deals.scss";

function projectName(project: TriyoProject): string {
  return project.Name || project.ProjectName || project.Title || "Untitled deal";
}

function projectEndDate(project: TriyoProject): string {
  const raw = project.EndDate || project.DueDate || project.End;
  if (!raw) return "—";
  return formatLongCalendarDate(raw);
}

const DEAL_FILTERS: { id: ProjectCategory; label: string }[] = [
  { id: "Active", label: "Active" },
  { id: "Completed", label: "Completed" },
  { id: "Archived", label: "Archived" },
];

function ownerInitials(project: TriyoProject): string {
  if (project.OwnerInitials) return project.OwnerInitials.slice(0, 2).toUpperCase();
  const name = project.OwnerName || "";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Deals(): JSX.Element {
  const { user } = useAuth();
  const { canCreateDeals, canViewDealDetails, canEditDeals } = getWorkflowAccess(user);
  const [projects, setProjects] = useState<TriyoProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<ProjectCategory>("Active");
  const [query, setQuery] = useState("");
  const [spinning, setSpinning] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<TriyoProject | null>(null);
  const [dealDirty, setDealDirty] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(async (nextCategory: ProjectCategory) => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const rows = await ProjectsApi.getProjectsForCategory(nextCategory);
      if (requestId !== requestRef.current) return;
      setProjects(rows);
    } catch (reason: unknown) {
      if (requestId !== requestRef.current) return;
      setProjects([]);
      setError(reason instanceof Error ? reason.message : "Deals could not be loaded.");
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void load(category);
    return () => {
      requestRef.current += 1;
    };
  }, [category, load]);

  const startCreating = () => {
    if (getCreateDealDisplay() === "dialog") {
      void openCreateDealDialog().then((result) => {
        if (result === "unavailable") setCreating(true);
      });
      return;
    }
    setCreating(true);
  };

  const deals = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => {
      const name = projectName(p).toLowerCase();
      const company = (p.CompanyName ?? "").toLowerCase();
      return name.includes(q) || company.includes(q);
    });
  }, [projects, query]);

  const refresh = async () => {
    setSpinning(true);
    try {
      await load(category);
    } finally {
      setSpinning(false);
    }
  };

  const closeSelectedDeal = useCallback(() => {
    if (dealDirty && !window.confirm("Discard your unsaved Deal changes?")) return;
    setDealDirty(false);
    setSelectedDeal(null);
  }, [dealDirty]);

  if (creating) {
    return (
      <div className="tab-panel">
        <TabSectionHeader
          title="Create Deal"
          action={
            <button type="button" className="deals__back" onClick={() => setCreating(false)}>
              Back
            </button>
          }
        />
        <CreateDeal onClose={() => setCreating(false)} onCreated={() => void load("Active")} />
      </div>
    );
  }

  if (selectedDeal) {
    const editable = canEditDeals && hasDealRelationship(selectedDeal, user);
    return (
      <div className="tab-panel">
        <TabSectionHeader
          title={editable ? "Edit Deal" : "Deal Details"}
          action={
            <button type="button" className="deals__back" onClick={closeSelectedDeal}>
              Back
            </button>
          }
        />
        <DealDetails
          summary={selectedDeal}
          editable={editable}
          onClose={closeSelectedDeal}
          onDirtyChange={setDealDirty}
          onUpdated={() => void load(category)}
        />
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <TabSectionHeader
        title="Deals"
        action={
          <button
            type="button"
            aria-label="Refresh"
            title="Refresh"
            className={`icon-button${spinning ? " icon-button--spin" : ""}`}
            disabled={spinning}
            aria-busy={spinning}
            onClick={() => void refresh()}
          >
            <ArrowSyncRegular />
          </button>
        }
      />

      <div className="search-row">
        <div className="search-field">
          <SearchRegular />
          <input
            className="search-field__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for Deals"
            aria-label="Search for Deals"
          />
        </div>
        {canCreateDeals && category === "Active" ? (
          <button type="button" className="add-button" aria-label="New Deal" title="New Deal" onClick={startCreating}>
            <AddRegular />
          </button>
        ) : null}
      </div>

      <div className="list-tabs" role="tablist" aria-label="Deal status">
        {DEAL_FILTERS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={category === id}
            className={`list-tab${category === id ? " list-tab--active" : ""}`}
            onClick={() => setCategory(id)}
          >
            {label}
            {category === id && !loading ? <span className="list-tab__count">{projects.length}</span> : null}
          </button>
        ))}
      </div>

      <div className="list-scroll">
        {loading ? (
          <div className="empty-state" role="status" aria-live="polite">
            <FluentSpinner label="Loading deals…" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="empty-state" role="alert">
            {error}
          </div>
        ) : null}

        {!loading && !error && deals.length === 0 ? (
          <div className="empty-state" role="status">
            No Deals{query ? " match your search" : ""}
          </div>
        ) : null}

        {!loading &&
          !error &&
          deals.map((deal) => {
            const initials = ownerInitials(deal);
            const canOpen = (canViewDealDetails || canEditDeals) && hasDealRelationship(deal, user);
            const actionLabel = canEditDeals ? "Edit" : "View";
            return (
              <article key={deal.Id ?? deal.ProjectId ?? projectName(deal)} className="deal-card">
                <div className="deal-card__heading">
                  <div className="deal-card__name">{projectName(deal)}</div>
                  {canOpen ? (
                    <button
                      type="button"
                      className="deal-card__action"
                      aria-label={`${actionLabel} ${projectName(deal)}`}
                      title={`${actionLabel} Deal`}
                      onClick={() => {
                        setDealDirty(false);
                        setSelectedDeal(deal);
                      }}
                    >
                      {canEditDeals ? <EditRegular /> : <EyeRegular />}
                    </button>
                  ) : null}
                </div>
                <div className="deal-card__meta">
                  <span className="deal-card__date">End Date: {projectEndDate(deal)}</span>
                  {initials ? <span className="deal-card__avatar">{initials}</span> : null}
                </div>
              </article>
            );
          })}
      </div>
    </div>
  );
}
